import React, { ChangeEvent, FC, useEffect, useMemo, useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useHistory, useParams, useLocation } from 'react-router'
import { yupResolver } from '@hookform/resolvers'
import { Select, ISelectOption } from 'components/forms/Selects'
import { isEmpty } from 'lodash'
import { CurrencyInputTransparent } from 'transfix-ui/components/CurrencyInputTransparent'
import { PhoneInput } from 'transfix-ui/components/PhoneInput'
import { Box, Grid } from 'transfix-ui/core'
import { useDebouncedCallback } from 'use-debounce/lib'
import { IMarketplaceBid } from 'utils/api/marketplace'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import * as yup from 'yup'
import { getInlineMessageProps } from 'apps/fleet-planner/components/modals/CreateLoadOverlay'
import { FullPageOverlay } from 'apps/fleet-planner/components/modals/full-page-overlay/FullPageOverlay'
import { useUserContext } from 'apps/fleet-planner/context'
import { useTrackConversionContext } from 'apps/fleet-planner/context/track-conversion-source'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useGuidedBidsMeetsThreshold } from 'apps/fleet-planner/types'
import { Button } from 'transfix-ui'
import { formatPhone, unformatPhone } from 'utils/formatPhone'

export const NewBidSchema = yup
  .object({
    motorCarrierDotNumber: yup.number().required('Please select a Carrier'),
    // Handles an edge case where MC# is null
    motorCarrierMcNumber: yup.number().nullable().notOneOf([undefined], 'Please select a Carrier'),
    amount: yup.string().required('This field is required'),
    carrierContactPhone: yup
      .string()
      .nullable()
      .required('This field is required')
      .min(10, 'Phone number is not valid'),
  })
  .required()

export type NewBidSchemaType = yup.InferType<typeof NewBidSchema>
export type TBidAction = 'submit' | 'delete'

interface IBidModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (action: TBidAction, id?: number) => void
  onError: (action: TBidAction) => void
  route?: string
}

export const BidModal: FC<IBidModalProps> = ({
  isOpen,
  onClose,
  onError,
  onSuccess,
  route = 'book',
}) => {
  const { trackEvent } = useFMSAnalytics()
  const history = useHistory()
  const { userData } = useUserContext()
  const { search } = useLocation()
  const { bidId, id, shipmentId: shipmentIdParam } = useParams<{
    shipmentId: string
    bidId: string
    id: string
  }>()
  // The Rate Con Page uses :id for the shipmentId
  const shipmentId = shipmentIdParam || id
  const { user, motorCarriers = [] } = userData || {}
  const { sourceChannel, sourceSegment } = useTrackConversionContext()
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isDeleteBidEnabled } = getFlag('fmsDeleteBid')
  const { enabled: isfmsGuidedBiddingEnabled } = getFlag('fmsGuidedBidding')
  // if a bid id is present, the modal is in edit state
  const inEditState = useMemo(() => !!bidId, [bidId])

  const [guidedBidMessage, setGuidedBidMessage] = useState<string>()
  const [isBidSubmitted, setIsBidSubmitted] = useState(false)
  const { mutate: meetsThreshold } = useGuidedBidsMeetsThreshold({})

  const getGuidedBidMessage = async (value: string) => {
    try {
      if (value) {
        const response = await meetsThreshold({
          bidAmount: value,
          shipmentId,
          sourceChannel,
          sourceSegment,
        })
        const { result } = response || {}
        // currently we don't want to display a message if the bid is within range
        if (result && result !== 'This bid is in range.') {
          trackEvent('Guided Bidding', 'GUIDED_BID_MESSAGE_SHOWN')
          setGuidedBidMessage(result)
        } else if (result) {
          setGuidedBidMessage(undefined)
        }
      }
    } catch (e) {
      // noop
    }
  }

  const [debouncedGetGuidedBidMessage] = useDebouncedCallback((value: string) => {
    getGuidedBidMessage(value)
  }, 500)

  // Option for Carrier select
  const carriersOptions = useMemo(
    () =>
      motorCarriers.map(mc => ({
        value: mc.dotNumber,
        label: mc.legalName,
      })),
    [motorCarriers]
  )

  const { phone } = user || {}

  const {
    activeShipmentDetailsId,
    editMarketplaceBid,
    errors: biddingErrors,
    removeMarketplaceBid,
    shipmentDetails,
    submitMarketplaceBid,
  } = useMarketplaceContext()

  const { bids = [] } = shipmentDetails || {}

  const getCurrentBid = useCallback(() => bids.find(b => b.id === Number(bidId)), [bidId, bids])

  const getCurrentBidMC = useCallback(() => {
    if (inEditState) {
      const currentBid = bids.find(b => b.id === Number(bidId))
      return motorCarriers.find(mc => mc.dotNumber === Number(currentBid?.carrierDotNumber))
    }
    if (motorCarriers.length === 1) {
      return motorCarriers[0]
    }
    return {
      dotNumber: undefined,
      mcNumber: undefined,
    }
  }, [bidId, bids, inEditState, motorCarriers])

  const { control, errors: formErrors, handleSubmit, setValue, watch } = useForm({
    mode: 'all',
    resolver: yupResolver(NewBidSchema),
    defaultValues: NewBidSchema.cast({
      amount: getCurrentBid()?.amount || '',
      motorCarrierDotNumber: getCurrentBidMC()?.dotNumber,
      motorCarrierMcNumber: getCurrentBidMC()?.mcNumber,
      carrierContactPhone: phone,
    }),
  })

  // Upon MC selection, check for existing bid for this carrier and set the appropriate bid amount
  const selectedMcDotNumber: number = watch('motorCarrierDotNumber')

  useEffect(() => {
    if (selectedMcDotNumber) {
      const currentBid = bids.find(b => Number(b.carrierDotNumber) === selectedMcDotNumber)
      if (currentBid && currentBid.status !== 'declined') {
        history.push(`/${route}/${shipmentId}/bid/${currentBid.id}${search}`)
        setValue('amount', currentBid?.amount!)
      } else if (inEditState) {
        // if we were editing an existing bid, remove the id from url
        history.push(`/${route}/${shipmentId}/bid${search}`)
        setValue('amount', '')
      }
    }
  }, [selectedMcDotNumber, shipmentId, bids, history, setValue, inEditState, route, search])

  const deleteBidHandler = useCallback(async () => {
    const successOrErrorHandler = (message: string | undefined) => {
      if (message === 'ok') {
        onSuccess('delete')
        trackEvent('Book Load', 'DELETED_EXISTING_BID_ON_BOOKABLE_LOAD')
      } else {
        onError('delete')
      }
      onClose()
    }
    const { message } = (await removeMarketplaceBid(bidId)) || {}
    successOrErrorHandler(message)
  }, [bidId, onSuccess, onClose, onError, removeMarketplaceBid, trackEvent])

  const submitHandler = useCallback(
    async (data: NewBidSchemaType) => {
      const currentBid = getCurrentBid()
      const newBid: IMarketplaceBid = {
        ...currentBid,
        ...data,
        shipmentId:
          activeShipmentDetailsId ||
          // strip all non-numeric value from the id for BE put request
          // e.g: "TFX-", "-d", etc
          Number(shipmentId.replace(/\D/g, '')),

        // Track conversion source
        sourceChannel,
        sourceSegment,
      } as IMarketplaceBid
      if (!sourceChannel || !sourceSegment) {
        trackEvent('Missing Attribution Source', 'SUBMITTED_BID_WITH_MISSING_ATTRIBUTION_SOURCE')
      }
      // Distinguish editing vs submitting a bid
      try {
        let response: IMarketplaceBid
        if (inEditState) {
          response = await editMarketplaceBid(newBid, Number(bidId))
        } else {
          response = await submitMarketplaceBid(newBid)
        }

        if (!response?.id) {
          throw new Error('Something went wrong')
        }

        onSuccess('submit', response.id)
        setIsBidSubmitted(true)
        trackEvent('Book Load', 'SUCCESSFULLY_SUBMITTED_BID_ON_BOOKABLE_LOAD', {
          sourceChannel,
          sourceSegment,
        })
      } catch (err) {
        onError('submit')

        const eventData = {
          error: err,
          formValues: data,
          shipmentId,
          sourceChannel,
          sourceSegment,
        }

        if (inEditState) {
          trackEvent('Book Load', 'BID_EDIT_FAILURE', eventData)
        } else {
          trackEvent('Book Load', 'BID_ADD_FAILURE', eventData)
        }
      } finally {
        setIsBidSubmitted(false)
        onClose()
      }
    },
    [
      getCurrentBid,
      activeShipmentDetailsId,
      shipmentId,
      sourceChannel,
      sourceSegment,
      trackEvent,
      inEditState,
      onSuccess,
      editMarketplaceBid,
      bidId,
      submitMarketplaceBid,
      onError,
      onClose,
    ]
  )
  const submitFormHandler = handleSubmit(submitHandler)
  const submitBidHandler = () => {
    setIsBidSubmitted(true)
    submitFormHandler()
  }

  const { isMobile } = useBreakpointContext()
  const inlineMessageProps = getInlineMessageProps(biddingErrors)
  return (
    <FullPageOverlay
      className='fp-bid-load-modal'
      title={inEditState ? 'Edit Bid' : 'Submit a Bid'}
      isOpen={isOpen}
      inlineMessageProps={inlineMessageProps}
      onClose={onClose}
      fullScreen={isMobile}
      showContentDividers={isMobile}
      maxWidth='xs'
      onSubmit={submitFormHandler}
      footer={
        <>
          {inEditState && isDeleteBidEnabled && (
            <>
              <Button fullWidth={isMobile} color='tertiary' onClick={deleteBidHandler} size='large'>
                Delete Bid
              </Button>
              {/* Empty div to place buttons on far left and far right */}
              <div style={{ flex: '1 0 0' }} />
            </>
          )}
          <Button
            data-test='submit-bid-form-cta'
            color='primary'
            fullWidth={isMobile}
            size='large'
            disabled={isBidSubmitted || !isEmpty(formErrors)}
            onClick={submitBidHandler}>
            {bidId ? 'Update Bid' : 'Submit Bid'}
          </Button>
        </>
      }>
      <Box marginTop={2}>
        <Grid container>
          <Grid item xs={12} md={12}>
            <Controller
              control={control}
              name='amount'
              render={({ onChange, value }) => {
                const changeHandler = ({ value: val }: { value: string }) => {
                  if (isfmsGuidedBiddingEnabled) {
                    debouncedGetGuidedBidMessage(val)
                  }
                  onChange(val)
                }
                // remove the decimal value from displayed value
                const bidAmount = value ? value.split('.')[0] : undefined
                return (
                  <CurrencyInputTransparent
                    onChange={changeHandler}
                    value={bidAmount}
                    validationMessage={formErrors?.amount?.message || guidedBidMessage || ''}
                    validationMessageColor='warning1'
                    invalid={
                      !!formErrors?.amount?.message ||
                      guidedBidMessage === 'This bid is high and is not likely to be accepted.'
                    }
                    autoFocus
                  />
                )
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <Box marginTop={carriersOptions.length > 1 ? 2 : 0}>
        <Grid container>
          <Grid item xs={12} md={12}>
            <Controller
              name='motorCarrierDotNumber'
              control={control}
              render={({ onBlur, onChange, value }) => {
                if (carriersOptions.length > 1) {
                  return (
                    <Select
                      dataTest='select-carrier-dropdown'
                      label='Select a Carrier'
                      large
                      required
                      options={carriersOptions}
                      onChange={({ value: val }: ISelectOption<number>) => {
                        onChange(val)
                        // Update motor carrier mc number
                        const selectedCarrier = motorCarriers.find(m => m.dotNumber === val)
                        setValue('motorCarrierMcNumber', selectedCarrier?.mcNumber!)
                        onBlur()
                      }}
                      value={value}
                      invalid={
                        !!formErrors?.motorCarrierDotNumber || !!formErrors?.motorCarrierMcNumber
                      }
                    />
                  )
                }
                /**
                 * In the case where there's only one carrier,
                 * we won't display the Select and select the default
                 */
                return <></>
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <Box marginTop={2}>
        <Grid container>
          <Grid item xs={12} md={12}>
            <Controller
              name='carrierContactPhone'
              defaultValue={phone}
              control={control}
              render={({ onBlur, onChange, value }) => {
                const formattedValue = formatPhone(value, { removeCountryCode: '+1' })
                const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
                  onChange(unformatPhone(e.target.value))
                }
                return (
                  <PhoneInput
                    className='fs-mask'
                    dataTest='phone-input'
                    label='Phone Number'
                    onChange={changeHandler}
                    onBlur={onBlur}
                    placeholder='(000) 000-0000'
                    value={formattedValue}
                    large
                    validationMessage={formErrors?.carrierContactPhone?.message}
                    invalid={!!formErrors?.carrierContactPhone?.message}
                    clearable={false}
                  />
                )
              }}
            />
          </Grid>
        </Grid>
      </Box>
      {/* Need this below to preserve the motorCarrierMcNumber value */}
      <Controller name='motorCarrierMcNumber' render={() => <></>} control={control} />
    </FullPageOverlay>
  )
}
